import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  Avatar,
  Chip,
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNotification } from '../../contexts/NotificationContext';
import especialistaService from '../../services/especialistaService';
import especialidadService from '../../services/especialidadService';
import usuarioService from '../../services/usuarioService';
import PageHeader from '../../components/common/PageHeader';
import ImageUpload from '../../components/forms/ImageUpload';

const EspecialistaForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(isEdit);
  const [previewImage, setPreviewImage] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [usuariosLoading, setUsuariosLoading] = useState(false);

  // Esquema de validación con Yup
  const validationSchema = Yup.object({
    usuario_id: Yup.number().required('El usuario es requerido'),
    dni: Yup.string().required('El DNI es requerido'),
    especialidades_ids: Yup.array().min(1, 'Debe seleccionar al menos una especialidad'),
    telefono: Yup.string(),
    whatsapp: Yup.string(),
    num_colegiatura: Yup.string(),
  });

  // Valores iniciales del formulario
  const initialValues = {
    usuario_id: '',
    dni: '',
    telefono: '',
    whatsapp: '',
    direccion: '',
    fecha_nacimiento: null,
    num_colegiatura: '',
    curriculum: '',
    notas: '',
    activo: true,
    especialidades_ids: [],
    porcentaje_comision: 0,
    fotoFile: null,
  };

  // Formik para manejar el formulario
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          await especialistaService.updateEspecialista(id, values);
          showNotification('Especialista actualizado con éxito', 'success');
        } else {
          await especialistaService.createEspecialista(values);
          showNotification('Especialista creado con éxito', 'success');
        }
        navigate('/especialistas');
      } catch (error) {
        console.error('Error saving specialist:', error);
        showNotification(
          `Error al ${isEdit ? 'actualizar' : 'crear'} el especialista`,
          'error'
        );
      }
    },
  });

  // Cargar datos para el formulario
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar especialidades
        const especialidadesResponse = await especialidadService.getEspecialidades();
        setEspecialidades(especialidadesResponse.data || []);

        // Cargar usuarios disponibles (solo aquellos que no tienen especialista asignado)
        const usuariosResponse = await usuarioService.getUsuariosDisponibles();
        setUsuarios(usuariosResponse.data || []);

        // Si es edición, cargar datos del especialista
        if (isEdit && id) {
          const especialistaResponse = await especialistaService.getEspecialista(id);
          const especialista = especialistaResponse.data;

          // Obtener IDs de especialidades seleccionadas
          const especialidadesIds = especialista.especialidades
            ? especialista.especialidades.map(esp => esp.id)
            : [];

          // Formatear datos para el formulario
          const formattedData = {
            ...especialista,
            especialidades_ids: especialidadesIds,
            fecha_nacimiento: especialista.fecha_nacimiento ? new Date(especialista.fecha_nacimiento) : null,
          };

          // Establecer usuario seleccionado
          setSelectedUsuario(especialista.usuario);

          // Establecer foto preview si existe
          if (especialista.foto) {
            setPreviewImage(process.env.REACT_APP_API_URL + especialista.foto);
          }

          // Actualizar formulario
          formik.setValues(formattedData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification('Error al cargar los datos', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit, showNotification]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buscar usuarios al escribir
  const handleUsuarioSearch = async (searchText) => {
    if (searchText.length < 3) return;

    setUsuariosLoading(true);
    try {
      const response = await usuarioService.getUsuarios(searchText);
      setUsuarios(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setUsuariosLoading(false);
    }
  };

  // Manejar selección de usuario
  const handleUsuarioChange = (event, usuario) => {
    setSelectedUsuario(usuario);
    if (usuario) {
      formik.setFieldValue('usuario_id', usuario.id);
    } else {
      formik.setFieldValue('usuario_id', '');
    }
  };

  // Manejar cambio de imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue('fotoFile', file);

      // Crear URL para preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title={isEdit ? 'Editar Especialista' : 'Nuevo Especialista'}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Especialistas', to: '/especialistas' },
          { text: isEdit ? 'Editar Especialista' : 'Nuevo Especialista' }
        ]}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Información del Usuario */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Información del Usuario
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        id="usuario-autocomplete"
                        options={usuarios}
                        getOptionLabel={(option) => `${option.nombre} ${option.apellidos} (${option.email})`}
                        value={selectedUsuario}
                        onChange={handleUsuarioChange}
                        onInputChange={(event, value) => handleUsuarioSearch(value)}
                        loading={usuariosLoading}
                        disabled={isEdit} // No permitir cambiar el usuario en edición
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Usuario *"
                            error={formik.touched.usuario_id && Boolean(formik.errors.usuario_id)}
                            helperText={formik.touched.usuario_id && formik.errors.usuario_id}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {usuariosLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        id="dni"
                        name="dni"
                        label="DNI *"
                        value={formik.values.dni}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.dni && Boolean(formik.errors.dni)}
                        helperText={formik.touched.dni && formik.errors.dni}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        id="num_colegiatura"
                        name="num_colegiatura"
                        label="Número de Colegiatura"
                        value={formik.values.num_colegiatura}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={formik.touched.especialidades_ids && Boolean(formik.errors.especialidades_ids)}
                      >
                        <InputLabel id="especialidades-label">Especialidades *</InputLabel>
                        <Select
                          labelId="especialidades-label"
                          id="especialidades_ids"
                          name="especialidades_ids"
                          multiple
                          value={formik.values.especialidades_ids}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          input={<OutlinedInput label="Especialidades *" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const esp = especialidades.find(e => e.id === value);
                                return (
                                  <Chip 
                                    key={value} 
                                    label={esp ? esp.nombre : value} 
                                    sx={{ bgcolor: esp ? esp.color : undefined, color: 'white' }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {especialidades.map((especialidad) => (
                            <MenuItem key={especialidad.id} value={especialidad.id}>
                              <Checkbox
                                checked={formik.values.especialidades_ids.indexOf(especialidad.id) > -1}
                              />
                              <ListItemText 
                                primary={especialidad.nombre} 
                                secondary={especialidad.descripcion}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.especialidades_ids && formik.errors.especialidades_ids && (
                          <FormHelperText>{formik.errors.especialidades_ids}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Fotografía */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fotografía
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Avatar
                      src={previewImage || '/assets/images/default-user.png'}
                      alt="Foto de especialista"
                      sx={{ width: 150, height: 150, mb: 2 }}
                    />

                    <ImageUpload
                      onChange={handleImageChange}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Información de Contacto */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Información de Contacto
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        id="telefono"
                        name="telefono"
                        label="Teléfono"
                        value={formik.values.telefono}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        id="whatsapp"
                        name="whatsapp"
                        label="WhatsApp"
                        value={formik.values.whatsapp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Fecha de Nacimiento"
                        value={formik.values.fecha_nacimiento}
                        onChange={(date) => formik.setFieldValue('fecha_nacimiento', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="direccion"
                        name="direccion"
                        label="Dirección"
                        value={formik.values.direccion}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Información Profesional */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Información Profesional
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        id="porcentaje_comision"
                        name="porcentaje_comision"
                        label="Porcentaje de Comisión (%)"
                        value={formik.values.porcentaje_comision}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        type="number"
                        InputProps={{
                          endAdornment: <Typography sx={{ ml: 1 }}>%</Typography>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="curriculum"
                        name="curriculum"
                        label="Curriculum"
                        value={formik.values.curriculum}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        rows={3}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="notas"
                        name="notas"
                        label="Notas Adicionales"
                        value={formik.values.notas}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Botones de acción */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ mr: 2 }}
                  onClick={() => navigate('/especialistas')}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : isEdit ? (
                    'Actualizar Especialista'
                  ) : (
                    'Crear Especialista'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      )}
    </Box>
  );
};

export default EspecialistaForm;